import {
  AnchoredPanelType,
  CheckBox,
  FieldSet,
  InfoBlockLayout,
  InfoBlockStatus,
  InputFormSidePanel,
  InputFormSidePanelProps,
  Select,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import { DbBackupConfig } from "../../gen/clients/mchub-azure-api-client-exa";
import { backupRetentionPeriods, backupSlots, getBackUpScheduleTimeRange } from "../../helpers/resourceHelper";

export const enum Fields {
  backups = "backups",
  retentionPeriod = "retention_period",
  scheduling = "scheduling",
}

// eslint-disable-next-line max-len
export interface ExaDbCdbBackupPanelProps extends Pick <InputFormSidePanelProps, "onClose" | "onSubmit" | "componentRef"> {
  targetId: string;
  cdbBackup: DbBackupConfig|undefined;
}

export const ExaDbCdbBackupEditPanel = ({
  targetId,
  cdbBackup,
  onSubmit,
  onClose,
  componentRef,
}: ExaDbCdbBackupPanelProps): JSX.Element => {
  const { locale } = React.useContext<Settings>(SettingsContext);
  const [showBackupOptions, setShowBackupOptions] = React.useState(cdbBackup?.autoBackupEnabled);
  const [defaultBackupSchedule,
    setDefaultBackupSchedule] = React.useState<string | undefined>(cdbBackup?.autoBackupWindow);
  const [defaultRetentionPeriod,
    setDefaultRetentionPeriod] = React.useState<string>((cdbBackup?.recoveryWindowInDays)?.toString()
    || backupRetentionPeriods.thirtyDaysPeriod.toString());
  const [key, setKey] = React.useState<string>();
  const info = {
    messageType: InfoBlockStatus.INFO,
    message: Messages.createExaDb.managementTab.info.backupInfo(),
    infoLayout: InfoBlockLayout.Compact,

  };

  const onChangeShowBackUpOptions = (value: boolean): void => {
    setShowBackupOptions(value);
    if (value) {
      setKey(uniqueGUID);
    }
  };

  const retentionOptions = Object.values(backupRetentionPeriods).map(period => ({
    id: `${period}`,
    text: `${period} ${Messages.common.days()}`,
  }));

  const scheduleOptions = backupSlots.map(schedule => ({
    id: schedule.id,
    text: getBackUpScheduleTimeRange(schedule.id, locale),
  }));

  return (
    <InputFormSidePanel
      anchorId={targetId}
      type={AnchoredPanelType.MEDIUM}
      title={Messages.createNewPanels.editBackup.title()}
      onSubmit={onSubmit}
      onClose={onClose}
      componentRef={componentRef}
    >
      <FieldSet
        header={Messages.labels.backup()}
      >
        <CheckBox
          defaultValue={showBackupOptions}
          statusInfo={info}
          fieldName={Fields.backups}
          label={Messages.labels.enableAutomaticBackups()}
          onChange={value => {
            onChangeShowBackUpOptions(value);
          }}
        />
        {showBackupOptions && (
          <>
            <Select
              key={key}
              defaultValue={defaultRetentionPeriod ? [defaultRetentionPeriod] : undefined}
              required
              fieldName={Fields.retentionPeriod}
              label={Messages.labels.backupRetentionPeriod()}
              options={retentionOptions}
              onChange={(id: string) => setDefaultRetentionPeriod(id)}
              hideSearchBox
            />
            <Select
              key={key}
              defaultValue={defaultBackupSchedule ? [defaultBackupSchedule] : undefined}
              required
              fieldName={Fields.scheduling}
              label={`${Messages.labels.backupScheduling()} (UTC)`}
              options={scheduleOptions}
              onChange={(id: string) => setDefaultBackupSchedule(id)}
              hideSearchBox
            />
          </>
        )}
      </FieldSet>
    </InputFormSidePanel>
  );
};
